import omit from 'lodash/omit';
import {Location} from 'react-router';
import pick from 'lodash/pick';

import {getQueryStringParam} from 'common/contexts/react/react-router';
import {BehaviorType} from 'common/query-builder/query-block/constants';

import {REPORT_LIMITS, ReportName} from '../constants';
import {QueryManager} from '../report-manager/query';
import {BaseQueryErrorType, ParamsAndResults} from '../types';
import {FeatureRolloutName, FeatureRollouts} from '../util/feature-sampling';
import {CurrentProject} from '../model-resources/projects';
import {
  BehaviorMetric,
  InsightsBookmarkParams,
  InsightsQueryError,
  InsightsQueryResults,
  InsightsResourceType,
} from './util/types';
import {addQueryLimitInfo, isCohortsQuery} from './util';

export class InsightsQueryManager extends QueryManager<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  public reportName = ReportName.Insights;
  public endpoint = `insights`;

  public getExtraCsvPostParams(bookmarkParams: InsightsBookmarkParams, currentProject: CurrentProject) {
    if (isCohortsQuery(bookmarkParams)) {
      return {};
    }

    return {
      queryLimits: {limit: REPORT_LIMITS[ReportName.Insights].csvExport},
      is_new_count_menu: FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProject),
    };
  }

  public getExtraPostParams({
    bookmarkParams,
    currentProject,
    location,
  }: {
    bookmarkParams: InsightsBookmarkParams;
    currentProject: CurrentProject;
    location: Location;
  }) {
    if (isCohortsQuery(bookmarkParams)) {
      return {};
    }

    const urlLimit = getQueryStringParam(location, `limit`);
    const limit = urlLimit ? parseInt(urlLimit) : REPORT_LIMITS[ReportName.Insights].query;
    // TODO(CC): Remove is_new_count_menu once insights new count menu is rolled out
    // This is a temporary solution to pass is_new_count_menu flag to qapi
    return {
      queryLimits: {limit},
      is_new_count_menu: FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProject),
    };
  }

  public transformBookmarkParams(bookmarkParams: InsightsBookmarkParams) {
    const showSections = bookmarkParams.sections.show;
    const transformedShowSections: BehaviorMetric[] = showSections.map((showClause) => {
      const behavior = {
        id: null,
        type: BehaviorType.Simple,
        ...omit(showClause, [`math`, `perUserAggregation`, `rolling`, `cumulative`, `property`]),
      };
      const measurement = {
        math: showClause.math,
        perUserAggregation: showClause.perUserAggregation,
        property: showClause.property,
        rolling: showClause.rolling,
        cumulative: showClause.cumulative,
      };
      return {
        behavior,
        measurement,
      };
    });

    return {
      ...bookmarkParams,
      sections: {
        ...bookmarkParams.sections,
        show: transformedShowSections,
      },
    };
  }

  public handleQueryError(err: BaseQueryErrorType): InsightsQueryError {
    return pick(err, [`data`, `status`, `error`, `formula_errors`]) as InsightsQueryError;
  }

  public handleResults({
    params,
    results,
  }: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults>): ParamsAndResults<
    InsightsBookmarkParams,
    InsightsQueryResults
  > {
    const isCohortQuery = params.sections.show.some((show) => show.resourceType === InsightsResourceType.Cohorts);
    if (!isCohortQuery) {
      results = addQueryLimitInfo(results);
    }

    return {params, results};
  }
}
